import { effect, signal, Signal, useComputed } from '@preact/signals-react';
import { useContext } from 'react';
import produce from 'immer';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { EditorContext } from '@/features/editor/context/editor-context';
import { CompoundChange } from '@/pkg/sdk';
import { iifeWrap } from '@/features/editor/widgets/code-editors/utils';
import {
  PLACEHOLDER_CSS,
  PENDING_PLACEHOLDER_HTML,
  PLACEHOLDER_JS,
} from '@/features/editor/widgets/ai-component/placeholders';
import { debounced } from '@/signals/utils';

export function useCompoundChangeRenderer(
  change: Signal<EditorDeclarativeBlock>,
  isProcessing: Signal<boolean>,
) {
  const {
    applyTempChange,
    devicePreview: { updateCompoundWidgetProps, iframeRef },
  } = useContext(EditorContext);

  const isReRenderRequired = signal(true);

  const isLegacyPlaceholder = useComputed(() =>
    (change?.value?.block?.value as CompoundChange)?.html?.includes(
      `__loomi_element_placeholder`,
    ),
  );

  const notifyState = () => {
    iframeRef?.current?.contentWindow?.postMessage(
      {
        tpe: `vsly-placeholder-state-changed`,
        state: isProcessing.value ? `loading` : `idle`,
      },
      `*`,
    );
  };

  effect(() => {
    isProcessing.value;
    setTimeout(notifyState, 100);
  });

  const { value: debouncedChange } = debounced(change, 100);

  const lastSyncedCode = signal<{ html: string; css: string; js: string }>({
    html: (change.peek().block.value as CompoundChange).html || ``,
    css: (change.peek().block.value as CompoundChange).css || ``,
    js: (change.peek().block.value as CompoundChange).js || ``,
  });

  effect(() => {
    const newChange = debouncedChange.value;

    if (newChange) {
      if (isLegacyPlaceholder.value) {
        const loadingChange = produce(newChange, (draft) => {
          const val = draft.block.value as CompoundChange;
          val.html = PENDING_PLACEHOLDER_HTML;
          val.css = PLACEHOLDER_CSS;
          val.js = PLACEHOLDER_JS;
        });
        applyTempChange(loadingChange);
        isReRenderRequired.value = false;
        return;
      }

      const value = newChange.block.value as CompoundChange;
      const newHtml = value.html;
      const newCss = value.css;
      const newJs = value.js;

      const isHtmlChanged = newHtml !== lastSyncedCode.peek().html;
      const isCssChanged = newCss !== lastSyncedCode.peek().css;
      const isJsChanged = newJs !== lastSyncedCode.peek().js;

      if (!isHtmlChanged && !isCssChanged && !isJsChanged) {
        console.debug(`CompoundChangeRenderer`, `No changes detected`, {
          lastSyncedCode,
          value,
        });
        return;
      }

      if (isReRenderRequired.value) {
        console.debug(
          `CompoundChangeRenderer`,
          `Applying change (full re-render)`,
          newChange,
        );
        const iifeChange = produce(newChange, (draft) => {
          const val = draft.block.value as CompoundChange;
          val.js = iifeWrap(val.js);
        });
        applyTempChange(iifeChange);
        isReRenderRequired.value = false;
      } else {
        console.debug(
          `CompoundChangeRenderer`,
          `Applying change (quick swap)`,
          newChange,
        );
        const div = document.createElement(`div`);
        div.innerHTML = value.html;
        updateCompoundWidgetProps(
          `#${newChange.editorId}`,
          isHtmlChanged || isJsChanged ? value.html : `noop`,
          isCssChanged ? value.css : `noop`,
          isHtmlChanged || isJsChanged ? iifeWrap(value.js) : `noop`,
        );
      }

      lastSyncedCode.value = {
        html: value.html,
        css: value.css,
        js: value.js,
      };
    }
  });

  const rerender = () => {
    isReRenderRequired.value = true;
  };

  return {
    rerender,
  };
}
